import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, ArrowRight, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const FeaturedMerchSection = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${BACKEND_URL}/api/public/shop/featured`);
        if (!cancelled) setItems(r.data || []);
      } catch {
        if (!cancelled) setItems([]);
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  // Hide the entire section if there are no featured items and we're not loading
  if (!loading && items.length === 0) return null;

  return (
    <section className="bg-white py-16 lg:py-24" data-testid="featured-merch-section">
      <div className="max-w-6xl mx-auto px-4">
        <div className="text-center mb-10">
          <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-[#BA1826] mb-2 inline-flex items-center gap-2">
            <ShoppingBag className="w-3.5 h-3.5" /> NHF Merch
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-[#0A0A0A] mb-3">
            Support Our Mission Through Merchandise
          </h2>
          <p className="text-base text-[#4B5563] max-w-2xl mx-auto">
            Every shirt, tote, and water bottle helps fund food drives, hygiene kits, and youth
            programs. Wear the mission. Carry the mission. Be part of it.
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-[#BA1826]" />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6" data-testid="featured-merch-grid">
              {items.map((item) => (
                <article key={item.id}
                  className="group bg-white border border-[#E5E7EB] rounded-lg overflow-hidden hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                  data-testid={`featured-merch-card-${item.id}`}>
                  <Link to={`/shop?item=${item.id}`} className="block">
                    <div className="aspect-square bg-[#FAFBFC] overflow-hidden">
                      {item.image ? (
                        <img src={item.image} alt={item.name}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-[#9CA3AF]">
                          <ShoppingBag className="w-10 h-10" />
                        </div>
                      )}
                    </div>
                  </Link>
                  <div className="p-3 lg:p-4 space-y-2">
                    <h3 className="font-heading font-bold text-sm lg:text-base text-[#0A0A0A] line-clamp-2 min-h-[2.5rem]"
                      data-testid={`featured-merch-name-${item.id}`}>
                      {item.name}
                    </h3>
                    <div className="flex items-center justify-between">
                      <span className="font-heading text-lg font-bold text-[#BA1826]"
                        data-testid={`featured-merch-price-${item.id}`}>
                        ${Number(item.price || 0).toFixed(2)}
                      </span>
                      {item.is_low_stock && (
                        <span className="text-[9px] font-bold uppercase tracking-wider bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded">
                          Almost gone
                        </span>
                      )}
                    </div>
                    {item.impact_blurb && (
                      <p className="text-[11px] text-[#4B5563] italic leading-snug border-l-2 border-[#BA1826]/30 pl-2"
                        data-testid={`featured-merch-impact-${item.id}`}>
                        {item.impact_blurb}
                      </p>
                    )}
                    <Link to={`/shop?item=${item.id}`}
                      className="w-full inline-flex items-center justify-center gap-1.5 bg-[#0A0A0A] text-white text-xs font-semibold py-2 rounded hover:bg-[#333] transition-colors"
                      data-testid={`featured-merch-quickshop-${item.id}`}>
                      Quick Shop <ArrowRight className="w-3 h-3" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>

            <div className="text-center mt-10">
              <Link to="/shop"
                className="inline-flex items-center gap-2 bg-[#BA1826] text-white px-7 py-3 rounded font-bold text-sm uppercase tracking-wider hover:bg-[#9F1421] transition-colors shadow-md hover:shadow-lg"
                data-testid="featured-merch-view-all">
                View All Merchandise <ArrowRight className="w-4 h-4" />
              </Link>
              <p className="text-[11px] text-[#9CA3AF] mt-3 italic max-w-md mx-auto">
                Merch purchases support our mission and are not tax-deductible donations.
              </p>
            </div>
          </>
        )}
      </div>
    </section>
  );
};

export default FeaturedMerchSection;
